import React, { useState } from 'react';
import { Line } from 'react-chartjs-2';
import { Chart as ChartJS, CategoryScale, LinearScale, PointElement, LineElement, Title, Tooltip, Legend } from 'chart.js';
import { formatCurrency } from '../utils/calculations';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import SEO from '../components/SEO';

ChartJS.register(CategoryScale, LinearScale, PointElement, LineElement, Title, Tooltip, Legend);

const RentVsBuyCalculator = () => {
  // Property details
  const [propertyPrice, setPropertyPrice] = useState(8000000);
  const [downPayment, setDownPayment] = useState(20);
  const [loanTenure, setLoanTenure] = useState(20);
  const [loanInterest, setLoanInterest] = useState(8.5);
  const [propertyAppreciation, setPropertyAppreciation] = useState(5);
  
  // Rent details
  const [monthlyRent, setMonthlyRent] = useState(25000);
  const [rentIncrease, setRentIncrease] = useState(5);
  
  // Other costs
  const [maintenanceBuy, setMaintenanceBuy] = useState(5000);
  const [propertyTax, setPropertyTax] = useState(15000);
  const [investmentReturn, setInvestmentReturn] = useState(12);
  
  const [years, setYears] = useState(20);
  const [result, setResult] = useState(null);

  const calculateEMI = (principal, rate, tenure) => {
    const monthlyRate = rate / 12 / 100;
    const months = tenure * 12;
    return principal * monthlyRate * Math.pow(1 + monthlyRate, months) / (Math.pow(1 + monthlyRate, months) - 1);
  };

  const calculate = () => {
    const downPaymentAmount = (propertyPrice * downPayment) / 100;
    const loanAmount = propertyPrice - downPaymentAmount;
    const emi = calculateEMI(loanAmount, loanInterest, loanTenure);
    
    let buyingCost = downPaymentAmount;
    let rentingCost = 0;
    let rentInvestmentValue = downPaymentAmount; // Invest down payment if renting
    let currentRent = monthlyRent;
    let currentMaintenance = maintenanceBuy;
    
    const yearlyData = [];
    const monthlyInvestReturn = investmentReturn / 12 / 100;
    
    for (let year = 1; year <= years; year++) {
      // Buying costs for the year
      const yearlyEMI = year <= loanTenure ? emi * 12 : 0;
      const yearlyMaintenance = currentMaintenance * 12;
      const yearlyPropertyTax = propertyTax * Math.pow(1.03, year - 1); // 3% increase
      
      buyingCost += yearlyEMI + yearlyMaintenance + yearlyPropertyTax;
      
      // Renting costs for the year
      const yearlyRent = currentRent * 12;
      rentingCost += yearlyRent;
      
      // Investment growth (difference between EMI and rent is invested)
      const monthlyDiff = (emi + currentMaintenance + propertyTax/12) - currentRent;
      if (monthlyDiff > 0) {
        for (let m = 0; m < 12; m++) {
          rentInvestmentValue = rentInvestmentValue * (1 + monthlyInvestReturn) + monthlyDiff;
        }
      } else {
        rentInvestmentValue = rentInvestmentValue * Math.pow(1 + monthlyInvestReturn, 12);
      }
      
      // Property value
      const propertyValue = propertyPrice * Math.pow(1 + propertyAppreciation / 100, year);
      
      // Calculate net worth
      const buyNetWorth = propertyValue - (year <= loanTenure ? loanAmount * (1 - year/loanTenure) : 0);
      const rentNetWorth = rentInvestmentValue;
      
      yearlyData.push({
        year,
        buyingCost,
        rentingCost,
        propertyValue,
        buyNetWorth,
        rentNetWorth,
        rentInvestmentValue,
      });
      
      // Increase rent and maintenance for next year
      currentRent *= (1 + rentIncrease / 100);
      currentMaintenance *= 1.05; // 5% maintenance increase
    }
    
    const finalPropertyValue = propertyPrice * Math.pow(1 + propertyAppreciation / 100, years);
    // totalRentPaid equals rentingCost, kept for clarity
    // eslint-disable-next-line no-unused-vars
    const totalRentPaid = rentingCost;
    const finalRentInvestment = rentInvestmentValue;
    
    const buyNetWorth = finalPropertyValue;
    const rentNetWorth = finalRentInvestment;
    
    const betterOption = buyNetWorth > rentNetWorth ? 'buy' : 'rent';
    const difference = Math.abs(buyNetWorth - rentNetWorth);

    setResult({
      downPaymentAmount,
      loanAmount,
      emi,
      totalBuyingCost: buyingCost,
      totalRentingCost: rentingCost,
      finalPropertyValue,
      finalRentInvestment,
      buyNetWorth,
      rentNetWorth,
      betterOption,
      difference,
      yearlyData,
    });
  };

  const formatCurrencyForPDF = (amount) => 'Rs. ' + Math.round(amount).toLocaleString('en-IN');

  const downloadPDF = () => {
    if (!result) return;
    const doc = new jsPDF();
    
    doc.setFontSize(18);
    doc.text('Rent vs Buy Analysis', 105, 20, { align: 'center' });
    
    doc.setFontSize(10);
    doc.text(`Generated on: ${new Date().toLocaleDateString('en-IN')}`, 105, 28, { align: 'center' });

    autoTable(doc, {
      startY: 40,
      head: [['Parameter', 'Buying', 'Renting']],
      body: [
        ['Initial Outflow', formatCurrencyForPDF(result.downPaymentAmount), formatCurrencyForPDF(0)],
        ['Monthly EMI/Rent', formatCurrencyForPDF(result.emi), formatCurrencyForPDF(monthlyRent)],
        [`Total Cost (${years} years)`, formatCurrencyForPDF(result.totalBuyingCost), formatCurrencyForPDF(result.totalRentingCost)],
        ['Final Asset Value', formatCurrencyForPDF(result.finalPropertyValue), formatCurrencyForPDF(result.finalRentInvestment)],
        ['Net Worth', formatCurrencyForPDF(result.buyNetWorth), formatCurrencyForPDF(result.rentNetWorth)],
      ],
      theme: 'striped',
      headStyles: { fillColor: [37, 99, 235] },
    });

    let finalY = doc.lastAutoTable.finalY + 15;
    doc.setFontSize(12);
    doc.text(`Recommendation: ${result.betterOption === 'buy' ? 'BUYING' : 'RENTING'} is better by ${formatCurrencyForPDF(result.difference)}`, 14, finalY);

    doc.save('Rent_vs_Buy_Analysis.pdf');
  };

  const chartData = result ? {
    labels: result.yearlyData.map(d => `Year ${d.year}`),
    datasets: [
      {
        label: 'Net Worth (Buying)',
        data: result.yearlyData.map(d => d.buyNetWorth),
        borderColor: '#10b981',
        backgroundColor: 'rgba(16, 185, 129, 0.1)',
        fill: false,
        tension: 0.4,
      },
      {
        label: 'Net Worth (Renting + Investing)',
        data: result.yearlyData.map(d => d.rentNetWorth),
        borderColor: '#3b82f6',
        backgroundColor: 'rgba(59, 130, 246, 0.1)',
        fill: false,
        tension: 0.4,
      },
    ]
  } : null;

  return (
    <div className="calculator-page">
      <SEO 
        title="Rent vs Buy Calculator"
        description="Free Rent vs Buy Calculator India - Compare renting vs buying a house. Make informed decision."
        keywords="rent vs buy calculator, rent or buy calculator India, home buying calculator, rent vs EMI calculator, housing decision calculator"
        canonicalPath="/rent-vs-buy"
      />
      <div className="calculator-header">
        <h1>🏠 Rent vs Buy Calculator</h1>
        <p>Should you rent or buy a house? Let's find out!</p>
      </div>

      <div className="calculator-container">
        <div className="calculator-inputs">
          <h4 style={{ marginBottom: '16px', color: '#1e293b' }}>🏠 Property Details</h4>
          
          <div className="input-group">
            <label>Property Price (₹)</label>
            <input type="number" value={propertyPrice === '' ? '' : propertyPrice} onChange={(e) => setPropertyPrice(e.target.value === '' ? '' : Number(e.target.value))} />
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px' }}>
            <div className="input-group">
              <label>Down Payment (%)</label>
              <input type="number" value={downPayment === '' ? '' : downPayment} onChange={(e) => setDownPayment(e.target.value === '' ? '' : Number(e.target.value))} />
            </div>
            <div className="input-group">
              <label>Loan Interest (%)</label>
              <input type="number" step="0.1" value={loanInterest === '' ? '' : loanInterest} onChange={(e) => setLoanInterest(e.target.value === '' ? '' : Number(e.target.value))} />
            </div>
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px' }}>
            <div className="input-group">
              <label>Loan Tenure (Years)</label>
              <input type="number" value={loanTenure === '' ? '' : loanTenure} onChange={(e) => setLoanTenure(e.target.value === '' ? '' : Number(e.target.value))} />
            </div>
            <div className="input-group">
              <label>Property Appreciation (%)</label>
              <input type="number" step="0.5" value={propertyAppreciation === '' ? '' : propertyAppreciation} onChange={(e) => setPropertyAppreciation(e.target.value === '' ? '' : Number(e.target.value))} />
            </div>
          </div>

          <h4 style={{ marginBottom: '16px', marginTop: '20px', color: '#1e293b' }}>🏢 Rent Details</h4>

          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px' }}>
            <div className="input-group">
              <label>Monthly Rent (₹)</label>
              <input type="number" value={monthlyRent === '' ? '' : monthlyRent} onChange={(e) => setMonthlyRent(e.target.value === '' ? '' : Number(e.target.value))} />
            </div>
            <div className="input-group">
              <label>Yearly Rent Increase (%)</label>
              <input type="number" step="0.5" value={rentIncrease === '' ? '' : rentIncrease} onChange={(e) => setRentIncrease(e.target.value === '' ? '' : Number(e.target.value))} />
            </div>
          </div>

          <h4 style={{ marginBottom: '16px', marginTop: '20px', color: '#1e293b' }}>📊 Other Parameters</h4>

          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px' }}>
            <div className="input-group">
              <label>Monthly Maintenance (₹)</label>
              <input type="number" value={maintenanceBuy === '' ? '' : maintenanceBuy} onChange={(e) => setMaintenanceBuy(e.target.value === '' ? '' : Number(e.target.value))} />
            </div>
            <div className="input-group">
              <label>Yearly Property Tax (₹)</label>
              <input type="number" value={propertyTax === '' ? '' : propertyTax} onChange={(e) => setPropertyTax(e.target.value === '' ? '' : Number(e.target.value))} />
            </div>
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px' }}>
            <div className="input-group">
              <label>Investment Return (%)</label>
              <input type="number" step="0.5" value={investmentReturn === '' ? '' : investmentReturn} onChange={(e) => setInvestmentReturn(e.target.value === '' ? '' : Number(e.target.value))} />
            </div>
            <div className="input-group">
              <label>Analysis Period (Years)</label>
              <input type="number" value={years === '' ? '' : years} onChange={(e) => setYears(e.target.value === '' ? '' : Number(e.target.value))} />
            </div>
          </div>

          <button className="btn btn-primary" style={{ width: '100%', marginTop: '10px' }} onClick={calculate}>
            Compare Rent vs Buy
          </button>
        </div>

        <div className="calculator-results">
          {result ? (
            <>
              <div className="result-summary" style={{ 
                background: result.betterOption === 'buy' 
                  ? 'linear-gradient(135deg, #10b981, #059669)'
                  : 'linear-gradient(135deg, #3b82f6, #2563eb)'
              }}>
                <h3>Recommendation</h3>
                <div className="amount">{result.betterOption === 'buy' ? '🏠 BUY' : '🏢 RENT'}</div>
                <p style={{ opacity: 0.9, marginTop: '8px' }}>
                  Better by {formatCurrency(result.difference)} over {years} years
                </p>
              </div>

              <div className="result-details">
                <h4 style={{ marginBottom: '12px' }}>Buying Analysis</h4>
                <div className="result-item">
                  <span className="label">Down Payment</span>
                  <span className="value">{formatCurrency(result.downPaymentAmount)}</span>
                </div>
                <div className="result-item">
                  <span className="label">Monthly EMI</span>
                  <span className="value">{formatCurrency(result.emi)}</span>
                </div>
                <div className="result-item">
                  <span className="label">Total Cost ({years} yrs)</span>
                  <span className="value">{formatCurrency(result.totalBuyingCost)}</span>
                </div>
                <div className="result-item" style={{ background: '#ecfdf5' }}>
                  <span className="label">Property Value ({years} yrs)</span>
                  <span className="value" style={{ color: '#10b981' }}>{formatCurrency(result.finalPropertyValue)}</span>
                </div>
              </div>

              <div className="result-details" style={{ marginTop: '16px' }}>
                <h4 style={{ marginBottom: '12px' }}>Renting Analysis</h4>
                <div className="result-item">
                  <span className="label">Current Monthly Rent</span>
                  <span className="value">{formatCurrency(monthlyRent)}</span>
                </div>
                <div className="result-item">
                  <span className="label">Total Rent ({years} yrs)</span>
                  <span className="value">{formatCurrency(result.totalRentingCost)}</span>
                </div>
                <div className="result-item" style={{ background: '#dbeafe' }}>
                  <span className="label">Investment Value ({years} yrs)</span>
                  <span className="value" style={{ color: '#2563eb' }}>{formatCurrency(result.finalRentInvestment)}</span>
                </div>
              </div>

              {chartData && (
                <div className="chart-container">
                  <h4>Net Worth Comparison</h4>
                  <Line data={chartData} options={{ 
                    responsive: true, 
                    plugins: { legend: { position: 'bottom' } },
                    scales: {
                      y: {
                        ticks: {
                          callback: (value) => '₹' + (value / 100000).toFixed(0) + 'L'
                        }
                      }
                    }
                  }} />
                </div>
              )}

              <button className="btn btn-secondary" style={{ width: '100%', marginTop: '20px' }} onClick={downloadPDF}>
                Download PDF Report
              </button>
            </>
          ) : (
            <div style={{ textAlign: 'center', padding: '40px 20px', color: '#64748b' }}>
              <p>Enter details to compare rent vs buy</p>
            </div>
          )}
        </div>
      </div>

      <div className="info-box">
        <h4>💡 Key Considerations</h4>
        <ul>
          <li>Buying makes sense if property appreciation &gt; investment returns + rent yield</li>
          <li>Consider stamp duty (5-7%) and registration costs in actual buying</li>
          <li>Rental yield in India is typically 2-3% of property value</li>
          <li>Factor in job mobility - buying makes sense for 7+ year stay</li>
        </ul>
      </div>
    </div>
  );
};

export default RentVsBuyCalculator;
